import { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Disclosure, Transition } from "@headlessui/react";
import {
  Bars3Icon,
  MagnifyingGlassIcon,
  ShoppingBagIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  UserIcon,
  HeartIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightOnRectangleIcon,
} from "@heroicons/react/24/outline";

import { Link, useLocation, useNavigate } from "react-router-dom";
import { Avatar, Button, Menu, MenuItem } from "@mui/material";
import { buildNavigation } from "../../../config/buildNavigation";
import api from "../../../config/api";
import AuthModal from "../Auth/AuthModal";
import { useDispatch, useSelector } from "react-redux";
import { getUser, logout } from "../../../Redux/Auth/Action";
import { getCart } from "../../../Redux/Customers/Cart/Action";
import { useCartDrawer } from "../Cart/CartDrawerContext";
import { useAuthModal } from "../Auth/AuthModalContext";

import LogoutDialog from "../../../Admin/LogoutDialog";
import "./Navigation.css";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

/* ---- premium mobile-drawer building blocks ---- */
const MobileItem = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm text-[#5e3e3e] transition hover:bg-[#f8ecd7] active:bg-[#f1e2c9]"
  >
    <span>{label}</span>
    <ChevronRightIcon className="h-3.5 w-3.5 text-[#cbb39d]" />
  </button>
);

const MobileAccountRow = ({ icon, label, onClick, border }) => (
  <button
    type="button"
    onClick={onClick}
    className={classNames(
      "flex w-full items-center gap-3 px-4 py-3 text-left transition active:bg-[#f8ecd7]",
      border ? "border-t border-[#f0e4cf]" : ""
    )}
  >
    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#f3e4cd] text-[#b28585]">
      {icon}
    </span>
    <span className="flex-1 text-sm font-medium text-[#511d00]">{label}</span>
    <ChevronRightIcon className="h-4 w-4 text-[#cbb39d]" />
  </button>
);

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { auth, cart } = useSelector((store) => store);
  const [anchorEl, setAnchorEl] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const mobileSearchRef = useRef(null);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const openUserMenu = Boolean(anchorEl);
  const jwt = localStorage.getItem("jwt");
  const location = useLocation();
  const { openDrawer } = useCartDrawer();
  // Shared so the cart can open sign-in at checkout too.
  const { open: openAuthModal, openAuthModal: handleOpen, closeAuthModal: handleClose } =
    useAuthModal();

  // Cart icon opens the slide-out mini-cart, except when already on the full
  // cart page (opening a drawer over it would be redundant).
  const handleCartClick = () => {
    if (location.pathname === "/cart") return;
    openDrawer();
  };

  // Storefront menu is built live from the categories in the DB — no hardcoded
  // menu. Starts empty and fills in once /api/categories/menu responds.
  const [navData, setNavData] = useState({ categories: [], pages: [] });
  const [announcements, setAnnouncements] = useState([]);
  const [brandName, setBrandName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [whatsappUrl, setWhatsappUrl] = useState("");

  useEffect(() => {
    let active = true;
    api
      .get("/api/categories/menu")
      .then(({ data }) => {
        const built = buildNavigation(data);
        if (active && built) setNavData(built);
      })
      .catch(() => {
        /* no categories available; menu stays empty */
      });
    // Announcement ticker messages from admin settings.
    api
      .get("/api/settings")
      .then(({ data }) => {
        if (!active) return;
        if (data?.announcements?.length) setAnnouncements(data.announcements);
        if (data?.brandName) setBrandName(data.brandName);
        if (data?.logoUrl) setLogoUrl(data.logoUrl);
        if (data?.contactPhone) setContactPhone(data.contactPhone);
        if (data?.whatsappUrl) setWhatsappUrl(data.whatsappUrl);
      })
      .catch(() => {
        /* keep fallback messages */
      });
    return () => {
      active = false;
    };
  }, []);

  // Repeat enough times that one scrolled half always overflows the screen,
  // keeping the loop seamless on wide displays.
  const tickerItems = [
    ...announcements,
    ...announcements,
    ...announcements,
    ...announcements,
  ];

  useEffect(() => {
    if (jwt) {
      dispatch(getUser(jwt));
      dispatch(getCart(jwt));
    }
  }, [dispatch, jwt]);

  useEffect(() => {
    dispatch(getCart(jwt));
  }, [dispatch, jwt]);

  useEffect(() => {
    if (cart) {
      setCartCount(cart?.cartItems?.length);
    }
  }, [cart]);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setOpen(false);
  };

  // Mobile navbar: expand-in-place search, then run it.
  const doMobileSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setMobileSearchOpen(false);
    setOpen(false);
  };

  // Auto-focus the input the moment the mobile search expands.
  useEffect(() => {
    if (mobileSearchOpen) {
      const t = setTimeout(() => mobileSearchRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [mobileSearchOpen]);

  const handleUserClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseUserMenu = (event) => {
    setAnchorEl(null);
  };

  const handleCategoryClick = (category, section, item, close) => {
    let url = `/${category.id}/${section.id}/${item.id}`;
    if (item.viewAll) {
      url = `${url}?view_all=true`;
    } else {
      url = `${url}?view_all=false`;
    }
    navigate(url);
    close();
  };

  // A top category with no sub-categories: link straight to its listing.
  const goToCategory = (category, close = () => {}) => {
    navigate(`/${category.id}/${category.id}/${category.id}?view_all=true`);
    close();
  };

  useEffect(() => {
    if (auth.user) {
      handleClose();
    }
    if (
      auth.user?.role !== "ADMIN" &&
      (location.pathname === "/login" || location.pathname === "/register")
    ) {
      navigate(-1);
    }
  }, [auth.user, handleClose, location.pathname, navigate]);

  // Ask for confirmation first (mirrors the admin side).
  const handleLogout = () => {
    handleCloseUserMenu();
    setOpen(false);
    setLogoutOpen(true);
  };

  const confirmLogout = () => {
    setLogoutOpen(false);
    dispatch(logout());
    navigate("/");
  };
  const handleMyOrderClick = () => {
    handleCloseUserMenu();
    setOpen(false);
    navigate("/account/order");
  };

  const handleMyProfileClick = () => {
    handleCloseUserMenu();
    setOpen(false);
    navigate("/account/profile");
  };

  const handleWishlistClick = () => {
    handleCloseUserMenu();
    setOpen(false);
    navigate("/wishlist");
  };

  function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
  }

  return (
    // Fragment, NOT a wrapping div: `position: sticky` can only move within its
    // parent's box, and the old <div> hugged the header exactly — so the header
    // had zero room to stick and scrolled away with it. As a fragment the
    // header becomes a direct child of the page-height layout div in
    // CustomerRoutes (MUI's ThemeProvider renders no DOM node), giving sticky
    // the whole page to travel over. `shadow-lg` moved onto the header.
    <>
      {/* Mobile menu */}
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-40 lg:hidden" onClose={setOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-[88%] max-w-sm flex-col overflow-y-auto bg-[#FFFBF2] shadow-2xl">
                {/* Header — gilded royal band */}
                <div className="sticky top-0 z-10 bg-gradient-to-br from-[#5e2a08] via-[#511d00] to-[#3a1400] px-5 pb-5 pt-6 text-[#f5e9d6] shadow-md">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-[#f1e2c9] backdrop-blur-sm transition hover:bg-white/20 active:scale-95"
                  >
                    <span className="sr-only">Close menu</span>
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>

                  {brandName && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-[#e0b65c]">✦</span>
                      <p className="font-display text-lg font-bold tracking-wide">{brandName}</p>
                    </div>
                  )}
                  <p className="mt-0.5 text-[10px] font-medium uppercase tracking-[0.3em] text-[#c9a86b]">
                    Royal Wedding Attire
                  </p>

                  <div className="mt-5">
                    {auth.user ? (
                      <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-[#e6c069] to-[#b8863b] text-lg font-extrabold text-[#3a1400] shadow-lg ring-2 ring-white/25">
                          {auth.user?.firstName?.[0]?.toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="text-[11px] text-[#c9a86b]">Namaste,</p>
                          <p className="truncate font-semibold">
                            {auth.user?.firstName} {auth.user?.lastName}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={handleOpen}
                        className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#e6c069] to-[#c99a3f] px-5 py-2.5 text-sm font-bold text-[#3a1400] shadow-lg transition active:scale-[0.98]"
                      >
                        <UserIcon className="h-4 w-4" /> Sign In / Register
                      </button>
                    )}
                  </div>
                </div>

                {/* Collections */}
                <div className="px-4 pt-5">
                  <p className="mb-2 px-1 text-[11px] font-bold uppercase tracking-[0.2em] text-[#b28585]">
                    Shop Collections
                  </p>
                  <div className="overflow-hidden rounded-2xl border border-[#eadcc0] bg-white/70">
                    {navData.categories.map((category, ci) => {
                      const hasMenu =
                        category.sections && category.sections.length > 0;
                      if (!hasMenu) {
                        return (
                          <button
                            key={category.id || category.name}
                            onClick={() => {
                              goToCategory(category);
                              setOpen(false);
                            }}
                            className={classNames(
                              "flex w-full items-center px-4 py-3.5 text-left transition active:bg-[#f8ecd7]",
                              ci ? "border-t border-[#f0e4cf]" : ""
                            )}
                          >
                            <span className="font-display text-[15px] font-semibold text-[#511d00]">
                              {category.name}
                            </span>
                          </button>
                        );
                      }
                      return (
                      <Disclosure key={category.id || category.name}>
                        {({ open: dOpen }) => (
                          <div className={ci ? "border-t border-[#f0e4cf]" : ""}>
                            <Disclosure.Button className="flex w-full items-center justify-between px-4 py-3.5 text-left transition active:bg-[#f8ecd7]">
                              <span className="font-display text-[15px] font-semibold text-[#511d00]">
                                {category.name}
                              </span>
                              <ChevronDownIcon
                                className={classNames(
                                  "h-4 w-4 text-[#b28585] transition-transform duration-200",
                                  dOpen ? "rotate-180" : ""
                                )}
                              />
                            </Disclosure.Button>
                            <Transition
                              enter="transition duration-200 ease-out"
                              enterFrom="opacity-0 -translate-y-1"
                              enterTo="opacity-100 translate-y-0"
                              leave="transition duration-150 ease-in"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                            >
                              <Disclosure.Panel className="px-2 pb-2">
                                {category.sections.length > 1 ? (
                                  category.sections.map((section) => (
                                    <div key={section.id || section.name} className="mb-1">
                                      <p className="px-3 pb-1 pt-2 text-[10.5px] font-bold uppercase tracking-wider text-[#c39b6a]">
                                        {section.name}
                                      </p>
                                      {section.items.map((item) => (
                                        <MobileItem
                                          key={item.name}
                                          label={item.name}
                                          onClick={() => {
                                            handleCategoryClick(category, section, item, () => {});
                                            setOpen(false);
                                          }}
                                        />
                                      ))}
                                    </div>
                                  ))
                                ) : (
                                  <div className="pt-1">
                                    {category.sections[0].items.map((item) => (
                                      <MobileItem
                                        key={item.name}
                                        label={item.name}
                                        onClick={() => {
                                          handleCategoryClick(category, category.sections[0], item, () => {});
                                          setOpen(false);
                                        }}
                                      />
                                    ))}
                                  </div>
                                )}
                              </Disclosure.Panel>
                            </Transition>
                          </div>
                        )}
                      </Disclosure>
                      );
                    })}
                  </div>
                </div>

                {/* Pages (About, Contact, …) */}
                {navData.pages?.length > 0 && (
                  <div className="px-4 pt-4">
                    <div className="overflow-hidden rounded-2xl border border-[#eadcc0] bg-white/70">
                      {navData.pages.map((page, i) => (
                        <Link
                          key={page.name}
                          to={page.href}
                          onClick={() => setOpen(false)}
                          className={classNames(
                            "flex items-center justify-between px-4 py-3 text-sm font-medium text-[#511d00] transition active:bg-[#f8ecd7]",
                            i ? "border-t border-[#f0e4cf]" : ""
                          )}
                        >
                          {page.name}
                          <ChevronRightIcon className="h-4 w-4 text-[#cbb39d]" />
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* My Account */}
                {auth.user && (
                  <div className="px-4 pt-5">
                    <p className="mb-2 px-1 text-[11px] font-bold uppercase tracking-[0.2em] text-[#b28585]">
                      My Account
                    </p>
                    <div className="overflow-hidden rounded-2xl border border-[#eadcc0] bg-white/70">
                      <MobileAccountRow
                        icon={<UserIcon className="h-[18px] w-[18px]" />}
                        label="My Profile"
                        onClick={handleMyProfileClick}
                      />
                      <MobileAccountRow
                        icon={<ShoppingBagIcon className="h-[18px] w-[18px]" />}
                        label="My Orders"
                        onClick={handleMyOrderClick}
                        border
                      />
                      <MobileAccountRow
                        icon={<HeartIcon className="h-[18px] w-[18px]" />}
                        label="My Wishlist"
                        onClick={handleWishlistClick}
                        border
                      />
                    </div>
                  </div>
                )}

                {/* Guests: the mobile header has no room for a wishlist icon
                    (it collided with the centred logo), so link it here. */}
                {!auth.user && (
                  <div className="px-4 pt-5">
                    <div className="overflow-hidden rounded-2xl border border-[#eadcc0] bg-white/70">
                      <MobileAccountRow
                        icon={<HeartIcon className="h-[18px] w-[18px]" />}
                        label="My Wishlist"
                        onClick={handleWishlistClick}
                      />
                    </div>
                  </div>
                )}

                {/* Concierge / styling help */}
                <div className="px-4 pt-5">
                  <div className="rounded-2xl border border-[#e8d4a8] bg-gradient-to-br from-[#fdf6e9] to-[#f6e8cf] p-4">
                    <p className="font-display text-sm font-bold text-[#511d00]">
                      Need styling help?
                    </p>
                    <p className="mt-0.5 text-xs text-[#8a7575]">
                      Talk to our wedding specialists.
                    </p>
                    {(whatsappUrl || contactPhone) && (
                      <div className="mt-3 flex gap-2">
                        {whatsappUrl && (
                          <a
                            href={whatsappUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-[#1f7a37]/12 py-2 text-xs font-bold text-[#1f7a37] transition active:scale-95"
                          >
                            <ChatBubbleLeftRightIcon className="h-4 w-4" /> WhatsApp
                          </a>
                        )}
                        {contactPhone && (
                          <a
                            href={`tel:${String(contactPhone).replace(/[^+0-9]/g, "")}`}
                            className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-[#511d00]/10 py-2 text-xs font-bold text-[#511d00] transition active:scale-95"
                          >
                            <PhoneIcon className="h-3.5 w-3.5" /> Call Us
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer / sign out */}
                <div className="mt-5 px-4 pb-7">
                  {auth.user && (
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="flex w-full items-center justify-center gap-2 rounded-full border border-[#e6cccc] bg-white py-2.5 text-sm font-bold text-[#b3403a] transition active:scale-[0.98]"
                    >
                      <ArrowRightOnRectangleIcon className="h-[18px] w-[18px]" /> Sign Out
                    </button>
                  )}
                  <div className="mt-4 flex items-center justify-center gap-2">
                    <span className="h-px w-8 bg-[#e0cdb0]" />
                    <span className="text-[#e0b65c]">✦</span>
                    <span className="h-px w-8 bg-[#e0cdb0]" />
                  </div>
                  <p className="mt-2 text-center text-[10px] font-medium uppercase tracking-[0.25em] text-[#b9a48f]">
                    Handcrafted in Rajasthan · Made in India
                  </p>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      <header className="text-[#511D00] bg-[#F8ECD7] shadow-lg sticky top-0 z-30 border-b border-[#e7d6b8]">
        {announcements.length > 0 && (
          <div className="announce-bar">
            <div className="announce-track">
              {tickerItems.map((msg, i) => (
                <span className="announce-item" key={i}>
                  <span className="announce-star">✦</span>
                  {msg}
                </span>
              ))}
            </div>
          </div>
        )}

        <nav
          // aria-label="Top"
          className="bg-[#F8ECD7]"
        >
          <div className="">
            {/* ---------- MOBILE NAVBAR ---------- */}
            <div className="lg:hidden">
              <div className="relative flex h-14 items-center justify-between px-4">
                {mobileSearchOpen ? (
                  /* expanded search */
                  <form onSubmit={doMobileSearch} className="flex w-full items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setMobileSearchOpen(false)}
                      aria-label="Close search"
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[#511d00] transition active:scale-95"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                    <div className="flex flex-1 items-center gap-2 rounded-full border border-[#e4d3b2] bg-white px-4 py-2 shadow-sm transition focus-within:border-[#b28585]">
                      <MagnifyingGlassIcon className="h-4 w-4 shrink-0 text-[#b28585]" />
                      <input
                        ref={mobileSearchRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search royal wedding wear…"
                        className="w-full bg-transparent text-sm text-[#511d00] placeholder-[#b3a3a3] focus:outline-none"
                      />
                      {searchQuery && (
                        <button
                          type="button"
                          onClick={() => {
                            setSearchQuery("");
                            mobileSearchRef.current?.focus();
                          }}
                          aria-label="Clear"
                          className="shrink-0 text-[#c9b3a3]"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </form>
                ) : (
                  <>
                    {/* menu */}
                    <button
                      type="button"
                      onClick={() => setOpen(true)}
                      aria-label="Open menu"
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-[#e4d3b2] bg-white/70 text-[#511d00] shadow-sm backdrop-blur-sm transition active:scale-95"
                    >
                      <Bars3Icon className="h-5 w-5" />
                    </button>

                    {/* logo — true-centered */}
                    <Link
                      to="/"
                      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                    >
                      <span className="sr-only">{brandName}</span>
                      {logoUrl ? (
                        <img
                          src={logoUrl}
                          alt={brandName}
                          /* Must stay UNDER the 56px (h-14) row: this link is
                             absolutely positioned and -translate-y-1/2 centred,
                             so anything taller spills out of the header rather
                             than stretching it. 44px leaves 6px clearance. */
                          className="h-[44px] max-h-[44px] w-auto max-w-[240px] object-contain"
                        />
                      ) : (
                        brandName && (
                          <span className="font-display text-xl font-bold text-[#511d00]">
                            {brandName}
                          </span>
                        )
                      )}
                    </Link>

                    {/* actions */}
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setMobileSearchOpen(true)}
                        aria-label="Search"
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-[#e4d3b2] bg-white/70 text-[#511d00] shadow-sm backdrop-blur-sm transition active:scale-95"
                      >
                        <MagnifyingGlassIcon className="h-5 w-5" />
                      </button>
                      <button
                        type="button"
                        onClick={handleCartClick}
                        aria-label="Cart"
                        className="relative flex h-10 w-10 items-center justify-center rounded-full border border-[#e4d3b2] bg-white/70 text-[#511d00] shadow-sm backdrop-blur-sm transition active:scale-95"
                      >
                        <ShoppingBagIcon className="h-5 w-5" />
                        {cartCount > 0 && (
                          <span className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-gradient-to-br from-[#b28585] to-[#5e3e3e] px-1 text-[10px] font-bold text-white shadow ring-2 ring-[#F8ECD7]">
                            {cartCount}
                          </span>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* ---------- DESKTOP NAVBAR ---------- */}
            <div className="hidden lg:flex h-16 items-center px-4 lg:px-11">
              {/* LEFT: mobile menu button + desktop category links */}
              <div className="flex flex-1 items-center self-stretch">
                <button
                  type="button"
                  className="rounded-md p-2 text-gray-400 lg:hidden"
                  onClick={() => setOpen(true)}
                >
                  <span className="sr-only">Open menu</span>
                  <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                </button>

                {/* Flyout menus (hover) */}
                <div className="hidden lg:block lg:self-stretch z-10">
                <div className="flex h-full space-x-8">
                  {navData.categories.map((category) => {
                    const hasMenu =
                      category.sections && category.sections.length > 0;
                    return (
                    <div
                      key={category.name}
                      className="group relative flex h-full items-center"
                    >
                      <button
                        onClick={() => goToCategory(category)}
                        className="text-[#511D00] relative z-10 flex items-center text-sm font-semibold uppercase tracking-wide outline-none transition-colors duration-200 hover:text-[#b28585] group-hover:text-[#b28585] after:absolute after:-bottom-1 after:left-0 after:h-[2px] after:w-0 after:rounded-full after:bg-[#b28585] after:transition-all after:duration-300 hover:after:w-full group-hover:after:w-full"
                      >
                        {category.name}
                        {/* Only for categories that actually have a flyout —
                            an arrow on a plain link would promise a menu that
                            never opens. Flips on hover, like the mobile drawer. */}
                        {hasMenu && (
                          <ChevronDownIcon
                            aria-hidden="true"
                            className="ml-1 h-4 w-4 transition-transform duration-200 group-hover:rotate-180"
                          />
                        )}
                      </button>

                      {hasMenu && (
                      <div className="invisible absolute left-0 top-full z-20 translate-y-1 pt-2 opacity-0 transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                        <div className="w-max overflow-hidden rounded-2xl border border-[#eadcc0] bg-white shadow-2xl">
                          <div className="px-8 py-7 text-sm">
                            {category.sections.length === 1 ? (
                              // single category -> flow items into columns (no empty space)
                              <ul className="max-w-[620px] columns-3 gap-x-14">
                                {category.sections[0].items.map((item) => (
                                  <li key={item.name} className="mb-3 break-inside-avoid">
                                    <p
                                      onClick={() =>
                                        handleCategoryClick(
                                          category,
                                          category.sections[0],
                                          item,
                                          () => {}
                                        )
                                      }
                                      className="inline-block cursor-pointer text-[#5e3e3e] transition-all hover:translate-x-1 hover:text-[#b28585]"
                                    >
                                      {item.name}
                                    </p>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div className="flex max-w-[880px] flex-wrap gap-x-12 gap-y-7">
                                {category.sections.map((section) => (
                                  <div key={section.name} className="min-w-[140px]">
                                    <p
                                      onClick={() =>
                                        handleCategoryClick(
                                          category,
                                          section,
                                          { id: section.id, viewAll: true },
                                          () => {}
                                        )
                                      }
                                      className="mb-4 cursor-pointer font-semibold uppercase tracking-wide text-[#511D00] transition-colors hover:text-[#b28585]"
                                    >
                                      {section.name}
                                    </p>
                                    <ul className="space-y-3">
                                      {section.items
                                        .filter((item) => !item.viewAll)
                                        .map((item) => (
                                          <li key={item.name}>
                                            <p
                                              onClick={() =>
                                                handleCategoryClick(
                                                  category,
                                                  section,
                                                  item,
                                                  () => {}
                                                )
                                              }
                                              className="inline-block cursor-pointer text-[#5e3e3e] transition-all hover:translate-x-1 hover:text-[#b28585]"
                                            >
                                              {item.name}
                                            </p>
                                          </li>
                                        ))}
                                    </ul>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      )}
                    </div>
                    );
                  })}

                  {navData.pages.map((page) => (
                    <Link
                      key={page.name}
                      to={page.href}
                      className="relative flex items-center text-sm font-semibold uppercase tracking-wide text-[#511D00] transition-colors hover:text-[#b28585] after:absolute after:-bottom-1 after:left-0 after:h-[2px] after:w-0 after:rounded-full after:bg-[#b28585] after:transition-all after:duration-300 hover:after:w-full"
                    >
                      {page.name}
                    </Link>
                  ))}
                </div>
              </div>
              {/* end LEFT */}
              </div>

              {/* CENTER: logo */}
              <div className="flex flex-shrink-0 justify-center">
                <Link to="/">
                  <span className="sr-only">{brandName}</span>
                  {/* Sized by height and capped under the 64px (h-16) nav row,
                      so a larger logo never changes the nav's height. */}
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={brandName}
                      className="h-[56px] max-h-[56px] w-auto max-w-[400px] object-contain"
                    />
                  ) : (
                    brandName && (
                      <span className="font-display text-2xl font-bold text-[#511d00]">
                        {brandName}
                      </span>
                    )
                  )}
                </Link>
              </div>

              {/* RIGHT: account, search & cart */}
              <div className="flex flex-1 items-center justify-end">
                <div className="hidden lg:flex lg:flex-1 lg:items-center lg:justify-end lg:space-x-6">
                  {auth.user && (
                    <div>
                      <Avatar
                        className="text-white"
                        onClick={handleUserClick}
                        aria-controls={open ? "basic-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? "true" : undefined}
                        sx={{
                          background:
                            "linear-gradient(135deg,#b28585 0%,#5e3e3e 100%)",
                          color: "white",
                          fontWeight: 700,
                          cursor: "pointer",
                          boxShadow: "0 4px 12px rgba(178,133,133,0.4)",
                        }}
                      >
                        {auth.user?.firstName[0].toUpperCase()}
                      </Avatar>

                      <Menu
                        id="basic-menu"
                        anchorEl={anchorEl}
                        open={openUserMenu}
                        onClose={handleCloseUserMenu}
                        MenuListProps={{
                          "aria-labelledby": "basic-button",
                        }}
                      >
                        <MenuItem onClick={handleMyProfileClick}>
                          My Profile
                        </MenuItem>

                        <MenuItem onClick={handleMyOrderClick}>
                          My Orders
                        </MenuItem>
                        <MenuItem onClick={handleWishlistClick}>
                          My Wishlist
                        </MenuItem>
                        <MenuItem onClick={handleLogout}>SignOut</MenuItem>
                      </Menu>
                    </div>
                  )}

                  {!auth.user && (
                    <Button
                      onClick={handleOpen}
                      variant="contained"
                      sx={{ borderRadius: "999px", px: 3, py: 0.8 }}
                    >
                      Sign in
                    </Button>
                  )}
                </div>

                {/* Search */}
                <form
                  onSubmit={handleSearch}
                  className="ml-3 hidden items-center rounded-full border border-[#d8c3c3] bg-white/70 px-3 py-1.5 transition-colors focus-within:border-[#b28585] md:flex"
                >
                  <MagnifyingGlassIcon className="h-4 w-4 text-[#b28585]" aria-hidden="true" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search…"
                    className="w-28 bg-transparent px-2 text-sm text-[#511d00] placeholder-[#b3a3a3] focus:outline-none lg:w-40"
                  />
                </form>
                <button
                  type="button"
                  onClick={() => navigate("/search")}
                  className="ml-2 p-2 text-[#5e3e3e] md:hidden"
                  aria-label="Search"
                >
                  <MagnifyingGlassIcon className="h-6 w-6" aria-hidden="true" />
                </button>

                {/* Wishlist — for everyone; guests' wishlist lives in the browser */}
                <div className="lg:flow-root lg:ml-6">
                  <Button
                    onClick={handleWishlistClick}
                    aria-label="Wishlist"
                    className="group -m-2 flex items-center p-2"
                    sx={{ minWidth: 0, borderRadius: "999px" }}
                  >
                    <HeartIcon className="h-6 w-6 text-[#5e3e3e]" aria-hidden="true" />
                  </Button>
                </div>

                {/* Cart */}
                <div className="lg:flow-root lg:ml-4">
                  <Button
                    onClick={handleCartClick}
                    className="group -m-2 flex items-center p-2"
                    sx={{ minWidth: 0, borderRadius: "999px" }}
                  >
                    <span className="relative inline-flex">
                      <ShoppingBagIcon
                        className="h-6 w-6 lg:flex-shrink-0 text-[#5e3e3e]"
                        aria-hidden="true"
                      />
                      {cartCount > 0 && (
                        <span className="absolute -top-2 -right-2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#b28585] px-1 text-[11px] font-bold text-white shadow">
                          {cartCount}
                        </span>
                      )}
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </nav>
      </header>
      <AuthModal
        handleClose={handleClose}
        open={openAuthModal}
        loginType="login"
      />
      <LogoutDialog
        open={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        onConfirm={confirmLogout}
        message="Are you sure you want to log out of your account?"
      />
    </>
  );
}
